/**
 * Define functions sending requests to the backend.
 *
 * Copyright © Ircam Amplify 2024-2026
 */

import { baseURL } from '@/config/env'
import axios from 'axios'

import { SortDirection } from '@/components/IampTable'
import { asyncUtil } from '@/config/asyncUtil'
import { logger, LogTag } from '@/config/logger'
import { FIELD_START_DATETIME } from '@/pages/TaskList/JobListDef'
import {
  setAimdTaskList,
  setAllowedCountries,
  setApiKeyList,
  setCompanyConsumption,
  setCompanyKpi,
  setInvoiceInfo,
  setJobList,
  setJobListSortDirection,
  setLastAimdTaskId,
  setLastJobId,
  setLastWebhookLogId,
  setPriceList,
  setServicesInfo,
  setUserInfo,
  setUserInfoComplete,
  setWebhookList,
  setWebhookLogs,
  store,
} from '@/store'
import { isDebugMockup } from '@/utils/TestUtil'
import {
  AsyncUtil,
  createTimeStampValue,
  DefaultInitialInvoiceListLength,
  isTooOld,
  JobDetail,
  TimeStampValue,
  UserInfo,
} from '@amplify3/duplo-back-end'
import { fetchAuthSession } from 'aws-amplify/auth'

/**
 * Create an Axios instance configured with the backend base URL and the
 * current user's Amplify auth token.
 * @param currentUser The current Amplify auth session.
 */
export const API = (currentUser: any) =>
  axios.create({
    baseURL: baseURL,
    headers: {
      Authorization: currentUser.tokens.idToken.toString(),
    },
  })

/** Retry `fn` on a transient (HTTP 500) failure. */
const retry = <T,>(fn: () => Promise<T>) => asyncUtil.retryOn500(fn)

/**
 * Batch of API requests to fetch data regarding the consumption of the user
 * (user information, consumption, KPI and invoices).
 *
 * The returned promise is shared through concurrent calls to handle race
 * condition.
 */
export const fetchConsumptionDataBatch = AsyncUtil.singleFlight(() => {
  return Promise.all([
    fetchUser(),
    fetchUserComplete(),
    fetchAimdTaskList(),
    fetchAllTaskList(),
    fetchCompanyConsumption(),
    fetchCompanyKpi(),
    fetchInvoice(),
    fetchPrice(),
  ])
})

/**
 * Batch of API requests at initial loading of the webapp.
 *
 * The returned promise is shared through concurrent calls to handle race
 * condition.
 */
export const fetchInitialBatch = AsyncUtil.singleFlight(() => {
  return Promise.all([
    store.dispatch(setJobListSortDirection(SortDirection.DESCENDING)),
    fetchConsumptionDataBatch(),
    fetchApiKeyList(),
    fetchServicesInfo(),
    fetchWebhooks(),
    fetchAllowedCountries(),
  ])
})

/** Fetch the list of allowed countries and store it in the state. */
export const fetchAllowedCountries = async () => {
  const { data } = await axios.create().get('/allowedCountries.json')
  store.dispatch(setAllowedCountries(data ?? []))
}

/**
 * Fetch the user's API key list from the back-end and update the store.
 * @param force If true, bypasses the cache and refetches unconditionally.
 */
export const fetchApiKeyList = async (force: boolean = false) => {
  try {
    if (force || isTooOld(store.getState().user.apiKeyList)) {
      const authSession = await fetchAuthSession()
      const { data } = await retry(() => API(authSession).get('/app'))
      logger.debug(LogTag.Util, '/app response:', data)
      store.dispatch(setApiKeyList(createTimeStampValue(data?.app_clients)))
    }
  } catch (err) {
    console.error('Error in fetchApiKeyList:', err)
    throw err
  }
}

/**
 * Delete an API key on the back-end.
 * @param apiClientId The client ID of the API key to delete.
 */
export const deleteApiKey = async (apiClientId: string) => {
  try {
    const authSession = await fetchAuthSession()
    const url = `/app/${apiClientId}`
    const { data } = await retry(() => API(authSession).delete(url))
    logger.debug(LogTag.Util, `delete ${url} response:`, data)
    return data
  } catch (err) {
    console.error('Error in deleteApiKey:', err)
    throw err
  }
}

/**
 * Fetch the company's consumption data and update the store.
 * @param force If true, bypasses the cache and refetches unconditionally.
 */
export const fetchCompanyConsumption = async (force: boolean = false) => {
  try {
    if (force || isTooOld(store.getState().user.companyConsumption)) {
      store.dispatch(setCompanyConsumption(undefined))
      const authSession = await fetchAuthSession()
      const { data } = await retry(() =>
        API(authSession).get('/company/consumption')
      )
      logger.debug(LogTag.Util, '/company/consumptison response:', data)
      store.dispatch(
        setCompanyConsumption(createTimeStampValue(data.consumption))
      )
    }
  } catch (err) {
    console.error('Error in fetchCompanyConsumption:', err)
    throw err
  }
}

/**
 * Fetch the company's KPI data and update the store. Uses a mock-up
 * response when debug mock-up mode is enabled.
 * @param force If true, bypasses the cache and refetches unconditionally.
 */
export const fetchCompanyKpi = async (force: boolean = false) => {
  try {
    if (force || isTooOld(store.getState().user.companyKpi)) {
      const authSession = await fetchAuthSession()
      let data

      if (isDebugMockup()) {
        // load a mock-up of API response for debug and tests
        const mock = await import('@/test/company_kpi_mockup.json')
        data = mock.default
      } else {
        // Request KPI from the backend
        const response = await retry(() =>
          API(authSession).get('/company/kpis')
        )
        data = response.data
      }

      if (isDebugMockup()) {
        // Warn the use of mock-up in the logs
        logger.warning(
          LogTag.Util,
          'Use test mockup for /company/kpis response:',
          data
        )
      } else {
        logger.debug(LogTag.Util, '/company/kpis response:', data)
      }
      store.dispatch(setCompanyKpi(createTimeStampValue(data?.kpis)))
    }
  } catch (err) {
    console.error('Error in fetchCompanyKpi:', err)
    throw err
  }
}

/**
 * Fetch invoices from the back-end and update the state apiInvoiceResponse.
 * @param previousId The ID of last fetched invoice to get the next ones.
 */
export async function fetchInvoice(
  previousId: string | null = null,
  force: boolean = false
) {
  try {
    if (force || isTooOld(store.getState().user.invoiceInfo)) {
      const params: Record<string, any> = {}
      params['limit'] = DefaultInitialInvoiceListLength
      if (previousId) {
        params['last_key'] = previousId
      }

      const authSession = await fetchAuthSession()
      const response = await retry(() =>
        API(authSession).get('/user/invoices', { params: params })
      )
      store.dispatch(setInvoiceInfo(createTimeStampValue(response?.data)))
    }
  } catch (err) {
    console.error('Error in fetchInvoice:', err)
    throw err
  }
}

interface fetchTaskListProps {
  /** Current cached job list, used to append new results when paginating. */
  currentList?: TimeStampValue<JobDetail[]>
  /** Redux action to save the fetched job list to the store. */
  setList: any
  /** Redux action to save the last fetched job ID to the store. */
  setLastId: any
  /** ID of the last fetched job, used as a cursor for pagination. */
  lastId?: string
  /** If true, replaces the existing list; if false, appends to it. */
  refreshing?: boolean
  /** Filter jobs by service names. Sent as a JSON array to the backend. */
  serviceNameList?: string[]
  /** Sort order applied to the job list. */
  sortDirection?: SortDirection
  /** Authenticated user info, used to build the client_id query param.
   * If not given the current user information is used by default.*/
  userInfo?: TimeStampValue<UserInfo>
}

/**
 * Request the list of tasks or jobs from the back-end.
 */
const fetchTaskList = async (props: fetchTaskListProps) => {
  try {
    let user = props.userInfo
    if (!user) {
      const { userInfo } = store.getState().user
      user = userInfo
    }

    const authSession = await fetchAuthSession()

    const sortDir = props.sortDirection ?? SortDirection.DESCENDING

    const lastJobParam =
      !props.refreshing && props.lastId?.length
        ? `&last_job_id=${props.lastId}`
        : ''

    const baseUrl =
      `/company/jobs?client_id=${user?.value.user_cognito_id}` +
      '&limit=10' +
      `&sort=["${FIELD_START_DATETIME}", "${sortDir}"]` +
      lastJobParam +
      '&reserved=with_cost_info'
    const url = props.serviceNameList
      ? baseUrl + `&service_name=${JSON.stringify(props.serviceNameList)}`
      : baseUrl

    const { data } = await retry(() => API(authSession).get(url))

    const jobsToSave = props.refreshing
      ? (data?.jobs ?? [])
      : [
          ...new Map(
            [...(props.currentList?.value ?? []), ...(data?.jobs ?? [])].map(
              (item) => [item?.job_id, item]
            )
          ).values(),
        ]

    const sortedJobs =
      props.sortDirection === SortDirection.ASCENDING
        ? jobsToSave.sort(
            (a: any, b: any) =>
              new Date(a?.job_start_datetime ?? 0).getTime() -
              new Date(b?.job_start_datetime ?? 0).getTime()
          )
        : jobsToSave.sort(
            (a: any, b: any) =>
              new Date(b?.job_start_datetime ?? 0).getTime() -
              new Date(a?.job_start_datetime ?? 0).getTime()
          )

    store.dispatch(props.setList(createTimeStampValue(sortedJobs)))
    store.dispatch(props.setLastId(data?.last_job_id))
  } catch (err) {
    console.error('Error in fetchTaskList:', err)
    throw err
  }
}

/**
 * Fetch the list of AI Music Detector tasks and update the store.
 * @param refreshing If true, replaces the existing list; if false, appends.
 */
export const fetchAimdTaskList = async ({
  refreshing = undefined,
}: {
  refreshing?: boolean
} = {}) => {
  const { aimdTaskList, lastAimdTaskId, userInfo, aimdTaskListSortDirection } =
    store.getState().user

  await fetchTaskList({
    currentList: aimdTaskList,
    lastId: lastAimdTaskId,
    refreshing: refreshing,
    serviceNameList: ['aidetector'],
    setList: setAimdTaskList,
    setLastId: setLastAimdTaskId,
    sortDirection: aimdTaskListSortDirection,
    userInfo: userInfo,
  })
}

/**
 * Fetch the list of all jobs/tasks and update the store.
 * @param refreshing If true, replaces the existing list; if false, appends.
 */
export const fetchAllTaskList = async ({
  refreshing = undefined,
}: {
  refreshing?: boolean
} = {}) => {
  const { jobList, lastJobId, userInfo, jobListSortDirection } =
    store.getState().user

  await fetchTaskList({
    currentList: jobList,
    lastId: lastJobId,
    refreshing: refreshing,
    setList: setJobList,
    setLastId: setLastJobId,
    sortDirection: jobListSortDirection,
    userInfo: userInfo,
  })
}

/**
 * Fetch the checkout price list and update the store.
 * @param force If true, bypasses the cache and refetches unconditionally.
 */
export const fetchPrice = async (force: boolean = false) => {
  try {
    if (force || isTooOld(store.getState().user.priceList)) {
      const authSession = await fetchAuthSession()
      const response = await retry(() =>
        API(authSession).get('/checkout/prices')
      )
      store.dispatch(setPriceList(createTimeStampValue(response?.data?.prices)))
    }
  } catch (err) {
    console.error('Error in fetchPrice:', err)
    throw err
  }
}

/**
 * Fetch information about available services and update the store.
 * @param force If true, bypasses the cache and refetches unconditionally.
 */
export const fetchServicesInfo = async (force: boolean = false) => {
  try {
    if (force || isTooOld(store.getState().user.servicesInfo)) {
      const authSession = await fetchAuthSession()
      const { data } = await retry(() => API(authSession).get('/services-info'))
      store.dispatch(setServicesInfo(createTimeStampValue(data)))
    }
  } catch (err) {
    console.error('Error in fetchServicesInfo:', err)
    throw err
  }
}

/**
 * Fetch the current user's information and update the store.
 * @param force If true, bypasses the cache and refetches unconditionally.
 */
export const fetchUser = async (force: boolean = false) => {
  try {
    if (force || isTooOld(store.getState().user.userInfo)) {
      const authSession = await fetchAuthSession()
      const { data } = await retry(() => API(authSession).get('/user'))
      store.dispatch(setUserInfo(createTimeStampValue(data?.user)))
    }
  } catch (err) {
    console.error('Error in fetchUser:', err)
    throw err
  }
}

/**
 * Fetch the current user's complete information (including extra fields)
 * and update the store.
 * @param force If true, bypasses the cache and refetches unconditionally.
 */
export const fetchUserComplete = async (force: boolean = false) => {
  try {
    if (force || isTooOld(store.getState().user.userInfoComplete)) {
      const authSession = await fetchAuthSession()
      const params: Record<string, any> = { all: true }
      const { data } = await retry(() =>
        API(authSession).get('/user', { params: params })
      )
      store.dispatch(setUserInfoComplete(createTimeStampValue(data)))
    }
  } catch (err) {
    console.error('Error in fetchUserComplete:', err)
    throw err
  }
}

/**
 * Request the sending of an invitation to create a sub-user.
 * @param company_id The company ID which will send the invitation.
 * @param email The email of the recipient.
 */
export async function inviteSubUser(company_id: string, email: string) {
  try {
    const authSession = await fetchAuthSession()
    const { data } = await API(authSession).post('/company/invite-user', {
      email,
      company_id,
    })
    logger.debug(`inviteSubUser: ${data}`)
    return data
  } catch (err) {
    console.error('Error in inviteSubUser:', err)
    throw err
  }
}

/**
 * Create a webhook on the back-end.
 * @param name The webhook's display name.
 * @param url The URL the webhook will call.
 * @param api_key_id The API key to associate the webhook with, if any.
 * @param secret The signing secret to associate with the webhook, if any.
 */
export const requestCreateWebhook = async (
  name: string,
  url: string,
  api_key_id?: string,
  secret?: string
) => {
  try {
    const authSession = await fetchAuthSession()
    const param: Record<string, string> = { name, url }

    if (secret && secret?.length > 0) {
      param['secret'] = secret
    }

    if (api_key_id && api_key_id?.length > 0) {
      param['client_id'] = api_key_id
    }

    const { data } = await API(authSession).post('/webhook', param)
    logger.debug(`createWebhook: name=${name} id=${data.webhook_id}`)
    return data
  } catch (err) {
    console.error('Error in createWebhook:', err)
    throw err
  }
}

/**
 * Fetch the user's webhook list from the back-end and update the store.
 * @param force If true, bypasses the cache and refetches unconditionally.
 */
export const fetchWebhooks = async (force: boolean = false) => {
  try {
    if (force || isTooOld(store.getState().user.webhookList)) {
      store.dispatch(setWebhookList(undefined))
      const authSession = await fetchAuthSession()
      const { data } = await API(authSession).get('/webhook')
      logger.debug(LogTag.Util, '/webhook:', data)
      store.dispatch(setWebhookList(createTimeStampValue(data?.webhooks)))
    }
  } catch (err) {
    console.error('Error in fetchWebhooks:', err)
    throw err
  }
}

/**
 * Delete a webhook on the back-end.
 * @param webhookId The ID of the webhook to delete.
 */
export const deleteWebhook = async (webhookId: string) => {
  try {
    const authSession = await fetchAuthSession()
    const { data } = await API(authSession).delete(`/webhook/${webhookId}`)
    return data
  } catch (err) {
    console.error('Error in deleteWebhook:', err)
    throw err
  }
}

/**
 * Fetch the logs of a webhook from the back-end and update the store.
 * @param webhookId The ID of the webhook whose logs are fetched.
 * @param refreshing If true, replaces the existing list; if false, appends.
 */
export const fetchWebhookLogs = async ({
  webhookId,
  refreshing,
}: {
  webhookId: string
  refreshing: boolean
}) => {
  try {
    const { webhookLogList, webhookLogListSortDirection, lastWebhookLogId } =
      store.getState().user
    const authSession = await fetchAuthSession()

    const sortDir = webhookLogListSortDirection ?? SortDirection.ASCENDING
    const lastLogParam =
      !refreshing &&
      lastWebhookLogId !== undefined &&
      lastWebhookLogId?.length > 0
        ? `&last_log_id=${lastWebhookLogId}`
        : ''
    const url =
      `webhook/${webhookId}/log?limit=10` +
      `&sort=["date", "${sortDir}"]` +
      lastLogParam
    const { data } = await retry(() => API(authSession).get(url))

    const logsToSave = refreshing
      ? (data?.webhook_logs ?? [])
      : [
          ...new Map(
            [...(webhookLogList ?? []), ...(data?.webhook_logs ?? [])].map(
              (item) => [item?.id, item]
            )
          ).values(),
        ]
    const sortedLogs =
      webhookLogListSortDirection === SortDirection.ASCENDING
        ? logsToSave.sort(
            (a: any, b: any) =>
              new Date(a?.date ?? 0).getTime() -
              new Date(b?.date ?? 0).getTime()
          )
        : logsToSave.sort(
            (a: any, b: any) =>
              new Date(b?.date ?? 0).getTime() -
              new Date(a?.date ?? 0).getTime()
          )

    store.dispatch(setWebhookLogs(sortedLogs))
    store.dispatch(setLastWebhookLogId(data?.last_log_id))
  } catch (err) {
    console.error('Error in fetchWebhookLogs:', err)
    throw err
  }
}
